import * as React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { MarkdownBody } from "../scene/MarkdownBody";
import { useContent, text, deck } from "../../hooks/useContent";
import { parseDeckReturn, stripDeckReturnParams } from "./deckReturn";

type Props = {
  /** Decks that were paid for — one key, or both for the bundle. */
  deckKeys: string[];
  open: boolean;
  onClose: () => void;
  portalHost?: HTMLElement | null;
};

/**
 * The order-confirmation screen itself. Presentational only: real PAID state is
 * set server-side by the shop webhook, so it never implies a payment on its own.
 */
export function DeckPaidConfirmation({ deckKeys, open, onClose, portalHost }: Props) {
  const { t } = useTranslation();
  const { data: content } = useContent();

  const decks = deckKeys
    .map((k) => deck(content, k))
    .filter((d): d is NonNullable<typeof d> => Boolean(d));
  const eyebrow =
    decks.length > 1
      ? text(content, "decks.bundle.title")
      : decks[0]?.title ?? "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={t("thankYou")}
      size="compact"
      container={portalHost ?? null}
    >
      <div className="flex flex-col items-center gap-5 py-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-[22px] text-primary">
          <Icon name="verified" title={t("thankYou")} />
        </span>
        <div className="max-w-[40ch]">
          <MarkdownBody
            surface="brand"
            body={text(content, "decks.checkout.success")}
          />
        </div>
        <Button
          variant="outline"
          surface="light"
          as="button"
          type="button"
          onClick={onClose}
        >
          {t("done")}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * URL-driven wrapper, mounted once at the app root: Stripe's success_url brings
 * the buyer back to the page they left with ?deck_paid=1&deck=<keys>, and this
 * turns that into the confirmation. Mounting it centrally means every page that
 * opens DeckCheckoutModal (/decks, /spirit-box, the product pages) is covered with no
 * wiring of its own. The params are stripped once shown, so a reload doesn't
 * re-open it.
 */
export function DeckPaidModal() {
  const [paidKeys, setPaidKeys] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    const keys = parseDeckReturn(window.location.search);
    if (!keys) return;
    setPaidKeys(keys);
    window.history.replaceState(
      {},
      "",
      stripDeckReturnParams(window.location.href),
    );
  }, []);

  return (
    <DeckPaidConfirmation
      deckKeys={paidKeys ?? []}
      open={paidKeys !== null}
      onClose={() => setPaidKeys(null)}
    />
  );
}
